import { ChangeDetectionStrategy, Component, EventEmitter, Input, OnChanges, Output, SimpleChanges } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { TranslateModule } from '@ngx-translate/core';
import { DialogComponent } from '@app/components/dialog/dialog.component';
import { PrimaryButton } from '@components/button/primary-button/primary-button';
import { SecondaryButton } from '@components/button/secondary-button/secondary-button';
import { InputComponent } from '@app/components/input/input.component';

/**
 * Manual coordinate entry for a photo with no auto-detected EXIF GPS (FR-004). There is no existing
 * Electronic Field Map counterpart to adapt this from — the Electronic Field Map upload simply blocks a photo with no
 * coordinates rather than offering manual entry, so this dialog is new.
 */
@Component({
  selector: 'app-geo-image-coordinate-modal',
  standalone: true,
  templateUrl: './geo-image-coordinate-modal.component.html',
  styleUrls: ['./geo-image-coordinate-modal.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [CommonModule, FormsModule, TranslateModule, DialogComponent, PrimaryButton, SecondaryButton, InputComponent]
})
export class GeoImageCoordinateModalComponent implements OnChanges {
  @Input() visible = false;

  @Output() visibleChange = new EventEmitter<boolean>();
  @Output() confirmed = new EventEmitter<{ latitude: number; longitude: number }>();

  latitude: number | null = null;
  longitude: number | null = null;
  showError = false;

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['visible'] && this.visible) {
      this.latitude = null;
      this.longitude = null;
      this.showError = false;
    }
  }

  get isValid(): boolean {
    // InputComponent's [type] property binding never activates Angular's NumberValueAccessor, so
    // latitude/longitude arrive here as strings at runtime despite being typed `number` — convert
    // explicitly rather than relying on the implicit coercion `>=`/`isNaN` happen to apply.
    const lat = Number(this.latitude);
    const lon = Number(this.longitude);
    return (
      this.latitude != null &&
      this.longitude != null &&
      !isNaN(lat) &&
      !isNaN(lon) &&
      lat >= -90 &&
      lat <= 90 &&
      lon >= -180 &&
      lon <= 180
    );
  }

  handleCancel(): void {
    this.visible = false;
    this.visibleChange.emit(false);
  }

  handleConfirm(): void {
    if (!this.isValid) {
      this.showError = true;
      return;
    }

    this.confirmed.emit({ latitude: Number(this.latitude), longitude: Number(this.longitude) });
    this.visible = false;
    this.visibleChange.emit(false);
  }
}
